import { parseConfigValue } from '~/settings/utils/configValueParser';

interface FieldWithMinMax {
  minValue?: string;
  maxValue?: string;
}

/**
 * Parse min/max from a field using parseFloat. Use for percentage and positive_float.
 */
export function parseNumericMinMax(field: FieldWithMinMax): {
  minNum: number | undefined;
  maxNum: number | undefined;
} {
  const minNum =
    field.minValue != null && field.minValue !== '' ? parseFloat(field.minValue) : undefined;
  const maxNum =
    field.maxValue != null && field.maxValue !== '' ? parseFloat(field.maxValue) : undefined;
  return { minNum, maxNum };
}

/**
 * Parse min/max from a field using parseConfigValue for size. Use for size type.
 */
export function parseSizeMinMax(field: FieldWithMinMax): {
  minNum: number | undefined;
  maxNum: number | undefined;
} {
  const minNum =
    field.minValue != null && field.minValue !== ''
      ? parseConfigValue(field.minValue, 'size').value
      : undefined;
  const maxNum =
    field.maxValue != null && field.maxValue !== ''
      ? parseConfigValue(field.maxValue, 'size').value
      : undefined;
  return { minNum, maxNum };
}

type ParseValueFn = (value: string | number) => number;

/**
 * Returns a react-hook-form validate function for min/max.
 * - Empty or invalid (NaN) input is allowed (returns true).
 * - Otherwise returns error string if parsed value is outside [minNum, maxNum].
 */
export function createMinMaxValidate(
  minNum: number | undefined,
  maxNum: number | undefined,
  parseValue: ParseValueFn
): (value: string | number) => true | string {
  return (value: string | number) => {
    const str = String(value ?? '').trim();
    if (str === '') {
      return true;
    }
    const num = parseValue(value);
    if (!Number.isFinite(num)) {
      return true;
    }
    if (minNum !== undefined && num < minNum) {
      return `Must be at least ${minNum}`;
    }
    if (maxNum !== undefined && num > maxNum) {
      return `Must be at most ${maxNum}`;
    }
    return true;
  };
}

/** Parser for percentage: strip % then parseFloat. */
export function parsePercentageValue(value: string | number): number {
  const str = String(value ?? '')
    .replace('%', '')
    .trim();
  return parseFloat(str);
}

/** Parser for positive_float: trim and parseFloat. */
export function parsePositiveFloatValue(value: string | number): number {
  const str = String(value ?? '').trim();
  return parseFloat(str);
}

/** Parser for size: parseConfigValue(str, 'size').value. */
export function parseSizeValue(value: string | number): number {
  const str = String(value ?? '').trim();
  if (!str) {
    return NaN;
  }
  return parseConfigValue(str, 'size').value;
}
